import { Camera, X } from 'lucide-react';
import { ClipboardEvent, useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import { useChatInteract, useConfig } from '@chainlit/react-client';

import { useTranslation } from '@/components/i18n/Translator';
import { PaperClip } from '@/components/icons/PaperClip';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

import { useUpload } from '@/hooks/useUpload';
import { cn } from '@/lib/utils';

import { Attachment } from '../MessageComposer/Attachment';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (message: string, fileReferences: { id: string }[]) => void;
}

interface LocalAttachment {
  id: string;
  serverId?: string;
  file: File;
  uploadProgress: number;
  uploaded: boolean;
  cancel?: () => void;
}

const MAX_QUESTIONS = 50;

const todayLabel = () =>
  new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

export default function MakeWorksheetDialog({ open, onOpenChange, onSubmit }: Props) {
  const { t } = useTranslation();
  const { config } = useConfig();
  const { uploadFile } = useChatInteract();

  const cameraInputRef = useRef<HTMLInputElement>(null);

  const [count, setCount] = useState<string>('10');
  const [description, setDescription] = useState<string>('');
  const [studentName, setStudentName] = useState<string>('');
  const [worksheetDate, setWorksheetDate] = useState<string>(todayLabel);
  const [includeAnswerKey, setIncludeAnswerKey] = useState<boolean>(true);
  const [workedSolutions, setWorkedSolutions] = useState<boolean>(false);
  const [emailToSelf, setEmailToSelf] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<LocalAttachment[]>([]);

  const fileSpec = useMemo(
    () => ({
      max_size_mb:
        config?.features?.spontaneous_file_upload?.max_size_mb || 500,
      max_files: config?.features?.spontaneous_file_upload?.max_files || 20,
      accept: config?.features?.spontaneous_file_upload?.accept || {
        'application/*': [],
        'audio/*': [],
        'image/*': [],
        'text/*': [],
        'video/*': []
      }
    }),
    [config]
  );

  const uploadEnabled = !!config?.features?.spontaneous_file_upload?.enabled;

  const handleFileUpload = useCallback(
    (files: File[]) => {
      const created: LocalAttachment[] = files.map((file) => {
        const id = uuidv4();
        const { xhr, promise } = uploadFile(file, (progress) => {
          setAttachments((prev) =>
            prev.map((a) =>
              a.id === id ? { ...a, uploadProgress: progress } : a
            )
          );
        });
        promise
          .then((res) => {
            setAttachments((prev) =>
              prev.map((a) =>
                a.id === id
                  ? {
                      ...a,
                      serverId: res.id,
                      uploaded: true,
                      uploadProgress: 100,
                      cancel: undefined
                    }
                  : a
              )
            );
          })
          .catch((err) => {
            toast.error(
              `${t('chat.fileUpload.errors.failed')} ${file.name}: ${
                typeof err === 'object' && err !== null
                  ? err.message ?? err
                  : err
              }`
            );
            setAttachments((prev) => prev.filter((a) => a.id !== id));
          });
        return {
          id,
          file,
          uploadProgress: 0,
          uploaded: false,
          cancel: () => {
            xhr.abort();
            setAttachments((prev) => prev.filter((a) => a.id !== id));
          }
        };
      });
      setAttachments((prev) => prev.concat(created));
    },
    [uploadFile, t]
  );

  const upload = useUpload({
    spec: fileSpec,
    onResolved: handleFileUpload,
    onError: (msg) => toast.error(msg),
    options: { noDrag: true }
  });

  const onPaste = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement>) => {
      if (!uploadEnabled) return;
      const items = event.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      Array.from(items).forEach((item) => {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      });
      if (files.length > 0) handleFileUpload(files);
    },
    [uploadEnabled, handleFileUpload]
  );

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.id === id);
      if (target?.cancel) target.cancel();
      return prev.filter((a) => a.id !== id);
    });
  }, []);

  const resetForm = () => {
    setCount('10');
    setDescription('');
    setStudentName('');
    setWorksheetDate(todayLabel());
    setIncludeAnswerKey(true);
    setWorkedSolutions(false);
    setEmailToSelf(false);
    // Abort any in-flight uploads and drop refs.
    attachments.forEach((a) => a.cancel?.());
    setAttachments([]);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetForm();
    onOpenChange(next);
  };

  const n = parseInt(count.trim(), 10);
  const countValid = !Number.isNaN(n) && n >= 1 && n <= MAX_QUESTIONS;
  const countOutOfRange = count.trim() !== '' && !countValid;
  const hasPendingUpload = attachments.some((a) => !a.uploaded);
  const canSubmit =
    countValid && description.trim().length > 0 && !hasPendingUpload;

  const handleSubmit = () => {
    if (!canSubmit) return;
    const body = description.trim();
    const name = studentName.trim();
    const date = worksheetDate.trim();
    const answerKeyLine = !includeAnswerKey
      ? 'No answer key.'
      : workedSolutions
        ? 'Include an answer key with full worked solutions (show the steps for every problem, not just the final answers).'
        : 'Include an answer key (final answers only).';
    const lines = [
      `Make a worksheet with ${n} question${n === 1 ? '' : 's'}: ${body}.`,
      answerKeyLine
    ];
    if (name) {
      lines.push(`Put the student's name "${name}" in the page header (student_name).`);
    }
    if (date) {
      lines.push(`Put the date "${date}" in the page header (worksheet_date).`);
    }
    if (emailToSelf) {
      lines.push('Save it as a PDF and email it to me.');
    }
    if (attachments.length > 0) {
      lines.push(
        `I've attached ${attachments.length} image${attachments.length === 1 ? '' : 's'} as reference — use ${attachments.length === 1 ? 'it' : 'them'} as the model for problem style and format.`
      );
    }
    const fileReferences = attachments
      .filter((a) => !!a.serverId)
      .map((a) => ({ id: a.serverId! }));
    onSubmit(lines.join(' '), fileReferences);
    onOpenChange(false);
    resetForm();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Make Worksheet</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="worksheet-count">Number of questions (1–{MAX_QUESTIONS})</Label>
            <Input
              id="worksheet-count"
              type="number"
              min={1}
              max={MAX_QUESTIONS}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              aria-invalid={countOutOfRange || undefined}
            />
            {countOutOfRange && (
              <p className="text-xs text-destructive">
                Enter a number between 1 and {MAX_QUESTIONS}.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="worksheet-description">What kinds of questions?</Label>
            <div className="relative">
              <Textarea
                id="worksheet-description"
                rows={6}
                placeholder="e.g. Mixed multi-digit addition and subtraction word problems for a 3rd grader, with at least two involving money."
                className={cn(
                  'placeholder:text-muted-foreground/50',
                  uploadEnabled && upload ? 'pb-12' : ''
                )}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onPaste={onPaste}
              />
              {uploadEnabled && upload && (
                <>
                  <input
                    id="worksheet-upload-input"
                    className="hidden"
                    {...upload.getInputProps()}
                  />
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length > 0) handleFileUpload(files);
                      e.target.value = '';
                    }}
                  />
                  <div className="absolute bottom-2 right-2 flex items-center -space-x-1">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="hover:bg-muted"
                            {...upload.getRootProps()}
                          >
                            <PaperClip className="!size-5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Attach images or files</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="hover:bg-muted"
                            onClick={() => cameraInputRef.current?.click()}
                          >
                            <Camera className="!size-5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Take a photo</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </>
              )}
            </div>
            {attachments.length > 0 && (
              <div className="flex flex-row flex-wrap gap-4">
                {attachments.map((a) => (
                  <Attachment
                    key={a.id}
                    name={a.file.name}
                    mime={a.file.type}
                    file={a.file}
                  >
                    <div className="absolute -right-2 -top-2">
                      <Button
                        size="icon"
                        type="button"
                        className="w-6 h-6 shadow-sm rounded-full border-4 bg-card hover:bg-card text-foreground light:border-muted"
                        onClick={() => removeAttachment(a.id)}
                      >
                        <X className="!size-3" />
                      </Button>
                    </div>
                  </Attachment>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-2">
              <Label htmlFor="worksheet-name">Name (optional)</Label>
              <Input
                id="worksheet-name"
                type="text"
                placeholder="e.g. Simon"
                className="placeholder:text-muted-foreground/50"
                value={studentName}
                onChange={(e) => setStudentName(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="worksheet-date">Date (optional)</Label>
              <Input
                id="worksheet-date"
                type="text"
                placeholder="e.g. April 23, 2026"
                className="placeholder:text-muted-foreground/50"
                value={worksheetDate}
                onChange={(e) => setWorksheetDate(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="worksheet-answer-key"
              checked={includeAnswerKey}
              onCheckedChange={(v) => {
                const on = v === true;
                setIncludeAnswerKey(on);
                // Worked solutions only apply when the key is on.
                if (!on) setWorkedSolutions(false);
              }}
            />
            <Label htmlFor="worksheet-answer-key" className="cursor-pointer">
              Include an answer key
            </Label>
          </div>

          {includeAnswerKey && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="worksheet-worked-solutions"
                checked={workedSolutions}
                onCheckedChange={(v) => setWorkedSolutions(v === true)}
              />
              <Label
                htmlFor="worksheet-worked-solutions"
                className="cursor-pointer"
              >
                Include worked solutions
              </Label>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Checkbox
              id="worksheet-email-self"
              checked={emailToSelf}
              onCheckedChange={(v) => setEmailToSelf(v === true)}
            />
            <Label htmlFor="worksheet-email-self" className="cursor-pointer">
              Email a PDF to me
            </Label>
          </div>

        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>Make worksheet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
